
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileUp, Loader2, X, Globe } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FileUploadProps {
  selectedFile: File | null;
  isLoading: boolean;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  onFileUpload: () => void;
  onUrlUpload: (url: string) => void;
}

// Supported file types with their MIME types
const SUPPORTED_FILE_TYPES = {
  '.c': 'text/x-c',
  '.cpp': 'text/x-c++',
  '.cs': 'text/x-csharp',
  '.css': 'text/css',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.go': 'text/x-golang',
  '.html': 'text/html',
  '.java': 'text/x-java',
  '.js': 'text/javascript',
  '.json': 'application/json',
  '.md': 'text/markdown',
  '.pdf': 'application/pdf',
  '.php': 'text/x-php',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.py': 'text/x-python',
  '.rb': 'text/x-ruby',
  '.sh': 'application/x-sh',
  '.tex': 'text/x-tex',
  '.ts': 'application/typescript',
  '.txt': 'text/plain'
};

// Maximum file size in bytes (16MB = 16 * 1024 * 1024 bytes)
const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB

const FileUpload = ({
  selectedFile,
  isLoading,
  onFileSelect,
  onFileRemove,
  onFileUpload,
  onUrlUpload
}: FileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const { toast } = useToast();

  const getFileExtension = (filename: string): string => {
    return '.' + filename.split('.').pop()?.toLowerCase() || '';
  };

  const isFileTypeSupported = (file: File): boolean => {
    const extension = getFileExtension(file.name);
    const supportedMimeTypes = Object.values(SUPPORTED_FILE_TYPES);
    
    // Check by extension first, then by MIME type
    return Object.keys(SUPPORTED_FILE_TYPES).includes(extension) || 
           supportedMimeTypes.includes(file.type);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type first
      if (!isFileTypeSupported(file)) {
        const supportedExtensions = Object.keys(SUPPORTED_FILE_TYPES).join(', ');
        toast({
          variant: "destructive",
          title: "Unsupported File Type",
          description: `We currently only support these file types: ${supportedExtensions}`,
        });
      } 
      // Check file size
      else if (file.size > MAX_FILE_SIZE) {
        toast({
          variant: "destructive",
          title: "File Too Large",
          description: `File size (${formatFileSize(file.size)}) exceeds the maximum limit of ${formatFileSize(MAX_FILE_SIZE)}. Please choose a smaller file.`,
        });
      } 
      // File is valid
      else {
        onFileSelect(file);
      }
    }
    
    // Clear the input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      toast({
        variant: "destructive",
        title: "Invalid URL",
        description: "Please enter a valid URL",
      });
      return;
    }

    if (!isValidUrl(urlInput.trim())) {
      toast({
        variant: "destructive",
        title: "Invalid URL",
        description: "Please enter a valid URL starting with http:// or https://",
      });
      return;
    }

    onUrlUpload(urlInput.trim());
    setUrlInput('');
    setShowUrlInput(false);
  };

  // Create accept attribute from supported extensions
  const acceptedFileTypes = Object.keys(SUPPORTED_FILE_TYPES).join(',');

  return (
    <div className="flex flex-col gap-2">
      {/* Two separate buttons */}
      <div className="flex items-center gap-2">
        {/* File Upload Button */}
        <Button 
          type="button"
          variant="outline"
          onClick={handleFileSelect}
          className={`flex items-center gap-2 p-2 h-8 rounded-lg group bg-gray-800/80 border-gray-600 hover:bg-gray-700/80 transition-colors ${selectedFile ? 'border-primary text-primary' : 'text-secondary hover:text-foreground'}`}
          title="Upload File"
        >
          <FileUp 
            className={`h-4 w-4 ${selectedFile ? 'text-primary' : 'text-secondary group-hover:text-foreground'} transition-colors`}
          />
          <span className={`text-xs ${selectedFile ? 'text-primary' : 'text-secondary group-hover:text-foreground'}`}>
            {selectedFile ? 'File Ready' : 'Upload File'}
          </span>
        </Button>

        {/* Ingest Web Button */}
        <Button 
          type="button"
          variant="outline"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="flex items-center gap-2 p-2 h-8 rounded-lg group bg-gray-800/80 border-gray-600 hover:bg-gray-700/80 transition-colors text-secondary hover:text-foreground"
          title="Ingest Web Content"
        >
          <Globe 
            className="h-4 w-4 text-secondary group-hover:text-foreground transition-colors"
          />
          <span className="text-xs text-secondary group-hover:text-foreground">
            Ingest Web
          </span>
        </Button>

        {/* Send File Button (when file is selected) */}
        {selectedFile && (
          <>
            <Button
              type="button"
              variant="default"
              onClick={onFileUpload}
              disabled={isLoading}
              className="rounded-lg h-8 text-xs"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Send File'
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={onFileRemove}
            >
              <X className="h-3 w-3" />
              <span className="sr-only">Remove file</span>
            </Button>
          </>
        )}
      </div>

      {/* URL Input (shown when Ingest Web is clicked) */}
      {showUrlInput && (
        <div className="flex items-center gap-2">
          <Input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Enter website URL (e.g., https://example.com)"
            className="flex-1 h-8 text-xs bg-gray-700 border-gray-600 text-white"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleUrlSubmit();
              }
            }}
            autoFocus
          />
          <Button
            type="button"
            variant="default"
            onClick={handleUrlSubmit}
            disabled={isLoading || !urlInput.trim()}
            className="rounded-lg h-8 text-xs"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Send URL'
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              setShowUrlInput(false);
              setUrlInput('');
            }}
            className="h-8 w-8"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept={acceptedFileTypes}
      />
    </div>
  );
};

export default FileUpload;
